import React, { useMemo, useState } from "react";
import { Printer, ArrowRight, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { PRINT_CONFIG, calculatePrint, formatINR } from "../lib/pricing";
import { useStudio } from "../context/StudioContext";
import { usePlanDrawer } from "../context/PlanDrawerContext";

export default function PrintingCalculator({ service, sub }) {
  const [sizeKey, setSizeKey] = useState(PRINT_CONFIG.sizes[1].key);
  const [gsmKey, setGsmKey] = useState(PRINT_CONFIG.gsm[0].key);
  const [methodKey, setMethodKey] = useState(PRINT_CONFIG.methods[0].key);
  const [coverKey, setCoverKey] = useState(PRINT_CONFIG.covers[0].key);
  const [pages, setPages] = useState(220);
  const [quantity, setQuantity] = useState(500);

  const result = useMemo(
    () => calculatePrint({ sizeKey, gsmKey, methodKey, coverKey, pages, quantity }),
    [sizeKey, gsmKey, methodKey, coverKey, pages, quantity]
  );

  const { addToPlan } = useStudio();
  const { openDrawer } = usePlanDrawer();

  const addRun = () => {
    const key = `03.1:${sizeKey}:${gsmKey}:${methodKey}:${coverKey}:${pages}:${quantity}`;
    addToPlan({
      key,
      code: sub.code,
      title: `${sub.title} (${result.breakdown.method}, ${quantity} copies)`,
      planName: `${result.breakdown.size} \u00b7 ${result.breakdown.gsm} \u00b7 ${result.breakdown.cover} \u00b7 ${result.breakdown.pages}pp`,
      service: service.label,
      price: formatINR(result.subtotal),
      billing: "PRINT RUN + GST",
      serviceSlug: service.slug,
      subSlug: sub.slug,
    });
    toast.success("Print run added to your plan.");
    openDrawer();
  };

  return (
    <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-[1fr_420px]">
      {/* Config form */}
      <div className="rounded-xl border border-brand-hair bg-brand-panel p-6">
        <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.28em] text-brand">
          <Sparkles className="h-3.5 w-3.5" /> Print Configurator
        </div>
        <div className="mt-3 font-serif-display text-[26px] font-medium leading-tight">
          Configure your print run
        </div>

        <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-2">
          {/* Page Size */}
          <Field label="Page size">
            <ChipGroup
              value={sizeKey}
              onChange={setSizeKey}
              options={PRINT_CONFIG.sizes.map((s) => ({ key: s.key, label: s.label }))}
            />
          </Field>

          {/* Cover */}
          <Field label="Cover">
            <ChipGroup
              value={coverKey}
              onChange={setCoverKey}
              options={PRINT_CONFIG.covers.map((c) => ({ key: c.key, label: c.label }))}
            />
          </Field>

          {/* GSM */}
          <Field label="Paper thickness">
            <ChipGroup
              value={gsmKey}
              onChange={setGsmKey}
              options={PRINT_CONFIG.gsm.map((g) => ({ key: g.key, label: g.label }))}
            />
          </Field>

          {/* Method */}
          <Field label="Print method">
            <ChipGroup
              value={methodKey}
              onChange={setMethodKey}
              options={PRINT_CONFIG.methods.map((m) => ({ key: m.key, label: m.label }))}
            />
          </Field>

          {/* Page count */}
          <Field label="Page count">
            <input
              type="number"
              min={1}
              max={1200}
              value={pages}
              onChange={(e) => setPages(e.target.value)}
              className="w-full rounded-md border border-brand-hair bg-brand-panel2 px-4 py-3 font-serif-display text-[18px] text-brand-ivory focus:border-brand"
            />
            <RangeHint>Typical novel: 200 - 320 pages</RangeHint>
          </Field>

          {/* Quantity */}
          <Field label="Quantity (copies)">
            <input
              type="number"
              min={1}
              max={100000}
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              className="w-full rounded-md border border-brand-hair bg-brand-panel2 px-4 py-3 font-serif-display text-[18px] text-brand-ivory focus:border-brand"
            />
            <RangeHint>Offset gets cheaper above 500 copies</RangeHint>
          </Field>
        </div>
      </div>

      {/* Live quote card */}
      <div className="sticky top-4 flex flex-col rounded-xl border border-brand-hair bg-brand-panel p-6">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-brand text-white">
            <Printer className="h-5 w-5" />
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-brand-mute">
              Live quote
            </div>
            <div className="font-serif-display text-[22px] leading-none">Instant estimate</div>
          </div>
        </div>

        <div className="mt-6 rounded-md border border-brand-hair bg-brand-panel2 p-5">
          <div className="flex items-baseline justify-between">
            <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-brand-mute">
              Per copy
            </div>
            <div className="font-serif-display text-[36px] leading-none text-brand-ivory">
              {formatINR(result.perCopy)}
            </div>
          </div>
          <div className="mt-3 space-y-1.5 text-[12.5px] text-brand-ivory/75">
            <Row label={`Interior (${result.breakdown.pages}pp \u00b7 ${result.breakdown.gsm} \u00b7 ${result.breakdown.method})`} value={formatINR(result.perCopy - PRINT_CONFIG.covers.find(c => c.key === coverKey).surcharge)} />
            <Row label={`Cover (${result.breakdown.cover})`} value={formatINR(PRINT_CONFIG.covers.find(c => c.key === coverKey).surcharge)} />
          </div>
        </div>

        <div className="mt-4 space-y-2 rounded-md border border-brand-hair bg-brand-panel2 p-5 text-[13px]">
          <Row label={`Copies \u00d7 per-copy (${quantity} \u00d7)`} value={formatINR(result.perCopy * quantity)} />
          {result.setup > 0 && (
            <Row label={`Offset setup fee`} value={formatINR(result.setup)} />
          )}
          <div className="my-2 h-px bg-brand-hair" />
          <Row label="Subtotal" value={formatINR(result.subtotal)} />
          <Row label="GST @ 18%" value={formatINR(result.gst)} muted />
          <div className="my-2 h-px bg-brand-hair" />
          <div className="flex items-baseline justify-between">
            <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-brand-mute">
              Total (incl. GST)
            </div>
            <div className="font-serif-display text-[26px] leading-none text-brand">
              {formatINR(result.total)}
            </div>
          </div>
        </div>

        <button
          onClick={addRun}
          className="group mt-5 flex w-full items-center justify-center gap-3 rounded-md bg-brand py-3.5 font-mono text-[12px] font-semibold uppercase tracking-[0.28em] text-white transition-colors hover:bg-[#ff5c26]"
        >
          Add this print run
          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
        </button>
        <div className="mt-3 text-center font-mono text-[10px] uppercase tracking-[0.28em] text-brand-mute">
          Press confirmed within 24 hours
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="mb-2 font-mono text-[10px] uppercase tracking-[0.28em] text-brand-mute">
        {label}
      </div>
      {children}
    </div>
  );
}

function ChipGroup({ value, onChange, options }) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => {
        const active = opt.key === value;
        return (
          <button
            key={opt.key}
            onClick={() => onChange(opt.key)}
            className={`rounded-md border px-3.5 py-2 text-[12.5px] transition-colors ${
              active
                ? "border-brand bg-brand/10 text-brand"
                : "border-brand-hair bg-brand-panel2 text-brand-ivory/80 hover:border-brand/40"
            }`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function RangeHint({ children }) {
  return (
    <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.24em] text-brand-mute">
      {children}
    </div>
  );
}

function Row({ label, value, muted }) {
  return (
    <div className={`flex items-baseline justify-between ${muted ? "text-brand-ivory/55" : "text-brand-ivory/85"}`}>
      <div className="text-[12.5px]">{label}</div>
      <div className="font-mono text-[12.5px]">{value}</div>
    </div>
  );
}
